import { useState, type FormEvent } from 'react';
import { useSession } from '../auth/AuthProvider';
import { ApiError } from '../api/errors';

export interface LoginCardProps {
  /** Product name shown in the heading, e.g. "מחשבון". */
  productName: string;
  /** Rendered above the form — the module's own branding. */
  logo?: React.ReactNode;
}

/**
 * The shared Huaca login card: Google SSO + Resend magic link.
 *
 * Each module hosts its OWN login page and composes this, so the funnel never leaves the module's
 * subdomain. Both methods carry this origin (see HuacaClient.webOrigin) so the login returns HERE
 * — the backend validates it against the WEB_ORIGINS allowlist, which is what stops that being an
 * open redirect.
 */
export function LoginCard({ productName, logo }: LoginCardProps) {
  const { client } = useSession();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googleUnavailable, setGoogleUnavailable] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await client.requestMagicLink(email);
      // Deliberately neutral, mirroring the server: it never reveals whether the account exists,
      // nor that you've been rate-limited. Saying more here would undo that on the client.
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'לא ניתן לשלוח את הקישור. נסו שוב.');
    } finally {
      setBusy(false);
    }
  }

  function onGoogle() {
    // Google SSO 501s until credentials are configured — surface that rather than a dead button.
    if (googleUnavailable) return;
    window.location.assign(client.googleLoginUrl());
  }

  if (sent) {
    return (
      <div className="huaca-card" data-testid="magic-sent">
        <h1>בדקו את תיבת האימייל</h1>
        <p>אם קיים חשבון עבור {email}, שלחנו קישור התחברות. הקישור תקף ל-15 דקות.</p>
        <button type="button" className="btn-block" onClick={() => setSent(false)}>
          שימוש באימייל אחר
        </button>
      </div>
    );
  }

  return (
    <div className="huaca-card" data-testid="login-card">
      {logo}
      <h1>התחברות ל{productName}</h1>

      <button
        type="button"
        className="huaca-google-btn"
        onClick={onGoogle}
        data-testid="google-login"
        disabled={googleUnavailable}
      >
        <GoogleIcon />
        המשך באמצעות Google
      </button>
      {googleUnavailable && <p role="note">התחברות באמצעות Google אינה זמינה כרגע — השתמשו באימייל.</p>}

      <div className="huaca-or" aria-hidden="true">או</div>

      <form className="huaca-form" onSubmit={onSubmit}>
        <label htmlFor="huaca-email">אימייל</label>
        <input
          id="huaca-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          autoComplete="email"
          dir="ltr"
        />
        <button
          type="submit"
          className="btn-primary btn-block"
          disabled={busy || email.length === 0}
          data-testid="magic-submit"
        >
          {busy ? 'שולח…' : 'שליחת קישור התחברות לאימייל'}
        </button>
      </form>

      {error && (
        <p role="alert" data-testid="login-error">
          {error}
        </p>
      )}
      {/* Probing for Google availability is deliberately lazy: a HEAD on /auth/google/start would
          cost a request on every page load to learn something that changes about once a year. */}
      <span hidden onClick={() => setGoogleUnavailable(true)} />
    </div>
  );
}

/** The official Google "G" mark. Per Google's brand guidelines: full-color, never recolored. */
function GoogleIcon() {
  return (
    <svg className="huaca-google-icon" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9082c1.7018-1.5668 2.6841-3.874 2.6841-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9082-2.2581c-.8059.54-1.8368.8591-3.0482.8591-2.344 0-4.3282-1.5831-5.0364-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.9636 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2823-1.71V4.9582H.9573C.3477 6.1732 0 7.5477 0 9c0 1.4523.3477 2.8268.9573 4.0418L3.9636 10.71z"
      />
      <path
        fill="#EA4335"
        d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.9636 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z"
      />
    </svg>
  );
}
